// HWP 5.0 Record 기반 바이너리 스트림 파서
// 각 레코드: TagID(10bit) + Level(10bit) + Size(12bit) + Data

export interface HwpRecord {
  tagId: number;
  level: number;
  size: number;
  data: Uint8Array;
}

// 주요 Tag IDs (DocInfo)
export const TAG = {
  DOCUMENT_PROPERTIES: 16,
  ID_MAPPINGS: 17,
  BIN_DATA: 18,
  FACE_NAME: 19,
  BORDER_FILL: 20,
  CHAR_SHAPE: 21,
  TAB_DEF: 22,
  NUMBERING: 23,
  BULLET: 24,
  PARA_SHAPE: 25,
  STYLE: 26,
  // BodyText
  PARA_HEADER: 66,
  PARA_TEXT: 67,
  PARA_CHAR_SHAPE: 68,
  PARA_LINE_SEG: 69,
  CTRL_HEADER: 71,
  PAGE_DEF: 72,
  FOOTNOTE_SHAPE: 73,
  PAGE_BORDER_FILL: 74,
  LIST_HEADER: 75,
  TABLE: 79,
  CTRL_DATA: 80,
  SHAPE_COMPONENT: 82,
} as const;

/** 바이너리 데이터에서 HWP 레코드 스트림 파싱 */
export function parseRecords(data: Uint8Array): HwpRecord[] {
  const records: HwpRecord[] = [];
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  let offset = 0;

  while (offset + 4 <= data.length) {
    const header = view.getUint32(offset, true);
    const tagId = header & 0x3ff;
    const level = (header >> 10) & 0x3ff;
    let size = (header >> 20) & 0xfff;

    offset += 4;

    // 확장 크기 (size가 0xFFF이면 다음 4바이트가 실제 크기)
    if (size === 0xfff) {
      if (offset + 4 > data.length) break;
      size = view.getUint32(offset, true);
      offset += 4;
    }

    if (offset + size > data.length) break;

    records.push({
      tagId,
      level,
      size,
      data: data.subarray(offset, offset + size),
    });

    offset += size;
  }

  return records;
}

/** 레코드 데이터에서 UTF-16LE 문자열 읽기 */
export function readUtf16(data: Uint8Array, offset: number, charCount: number): string {
  let str = '';
  for (let i = 0; i < charCount; i++) {
    const code = data[offset + i * 2] | (data[offset + i * 2 + 1] << 8);
    if (code === 0) break;
    str += String.fromCharCode(code);
  }
  return str;
}

/** 레코드 데이터에서 uint16 읽기 */
export function readU16(data: Uint8Array, offset: number): number {
  return data[offset] | (data[offset + 1] << 8);
}

/** 레코드 데이터에서 uint32 읽기 */
export function readU32(data: Uint8Array, offset: number): number {
  return (
    data[offset] |
    (data[offset + 1] << 8) |
    (data[offset + 2] << 16) |
    (data[offset + 3] << 24)
  ) >>> 0;
}

/** 레코드 데이터에서 int32 읽기 */
export function readI32(data: Uint8Array, offset: number): number {
  return data[offset] | (data[offset + 1] << 8) | (data[offset + 2] << 16) | (data[offset + 3] << 24);
}
